import { useState, useEffect, useCallback, useRef } from "react";

const STORAGE_PREFIX = "controlcash_form_";

/**
 * Hook que persiste el estado del formulario en sessionStorage.
 * Si cambias de pestaña y el navegador suspende la pestaña, al volver se restaura el estado.
 * @param {string} key - Identificador único del formulario (ej: "newmonth", "expense_add_123")
 * @param {*} initialState - Estado inicial si no hay datos guardados
 * @returns {[state, setState, clearPersisted, wasRestored]} - Estado, setter, función para limpiar, y si se restauró desde storage
 */
export function usePersistedFormState(key, initialState) {
  const storageKey = STORAGE_PREFIX + key;
  const wasRestoredRef = useRef(false);

  const [state, setState] = useState(() => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) {
        wasRestoredRef.current = true;
        return JSON.parse(saved);
      }
    } catch (e) {
      console.warn("usePersistedFormState: error al restaurar", e);
    }
    return initialState;
  });

  // Guardar en sessionStorage cuando cambie el estado (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(state));
      } catch (e) {
        console.warn("usePersistedFormState: error al guardar", e);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [state, storageKey]);

  const clearPersisted = useCallback(() => {
    try {
      sessionStorage.removeItem(storageKey);
    } catch (e) {}
  }, [storageKey]);

  return [state, setState, clearPersisted, wasRestoredRef.current];
}
